import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import { EndpointConfigList } from './EndpointConfigList';
import { useUrlCheckSocket } from './hooks/useUrlCheckSocket';
import { PostmanEnvConfig } from './PostmanEnvConfig';
import { UrlAvailabilityStatus } from './UrlAvailabilityStatus';
import styles from './Specifications.module.css';

interface SchemaProxyConfig {
  default_proxy_profile_id: string | null;
  endpoint_configs: Record<string, any>;
  context: string;
}

export const SpecificationDetail: React.FC = () => {
  const { schemaId } = useParams<{ schemaId: string }>();
  const navigate = useNavigate();

  const [schema, setSchema] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [proxyProfiles, setProxyProfiles] = useState<any[]>([]);
  const [schemaProxyConfig, setSchemaProxyConfig] = useState<SchemaProxyConfig>({
    default_proxy_profile_id: null,
    endpoint_configs: {},
    context: '',
  });
  const [schemaEndpoints, setSchemaEndpoints] = useState<any[]>([]);
  const [postmanEnv, setPostmanEnv] = useState<any>(null);
  const [targetUrl, setTargetUrl] = useState<string>('');
  const { checking, checkProgress, checkResult, triggerCheck, restoreStatus } = useUrlCheckSocket(schemaId);

  const extractEndpoints = useCallback((schemaData: any): void => {
    try {
      const content = typeof schemaData.content === 'string' ? JSON.parse(schemaData.content) : schemaData.content;
      const paths = content.paths || {};
      const endpoints: any[] = [];

      const resolveRef = (ref: string, root: any): any => {
        if (!ref || typeof ref !== 'string') return null;
        const parts = ref.split('/');
        let current = root;
        for (let i = 1; i < parts.length; i++) {
          current = current[parts[i]];
          if (!current) return null;
        }
        return current;
      };

      Object.keys(paths).forEach((pathStr) => {
        const pathItem = paths[pathStr];
        Object.keys(pathItem).forEach((method) => {
          if (!['get', 'post', 'put', 'delete', 'patch'].includes(method.toLowerCase())) return;
          const operation = pathItem[method];
          const parameters: any[] = [];

          if (operation.parameters) {
            operation.parameters.forEach((param: any) => {
              parameters.push({
                name: param.name,
                in: param.in,
                required: param.required || false,
                mleEncrypted: param['x-mle-encrypted'] || false,
              });
            });
          }

          if (operation.requestBody?.content) {
            const bodyContent = operation.requestBody.content['application/json'];
            if (bodyContent?.schema) {
              let bodySchema = bodyContent.schema;
              if (bodySchema.$ref) {
                bodySchema = resolveRef(bodySchema.$ref, content);
              }

              const extractProps = (s: any, propPath = ''): void => {
                if (!s?.properties) return;
                Object.keys(s.properties).forEach((propName) => {
                  const prop = s.properties[propName];
                  const fullPath = propPath ? `${propPath}.${propName}` : propName;
                  parameters.push({
                    name: fullPath,
                    in: 'body',
                    required: s.required?.includes(propName) || false,
                    mleEncrypted: prop['x-mle-encrypted'] || false,
                    type: prop.type || 'object',
                  });
                  if (prop.type === 'object' && prop.properties) extractProps(prop, fullPath);
                  if (prop.$ref) {
                    const resolved = resolveRef(prop.$ref, content);
                    if (resolved) extractProps(resolved, fullPath);
                  }
                });
              };
              extractProps(bodySchema);
            }
          }

          endpoints.push({ path: pathStr, method: method.toUpperCase(), parameters });
        });
      });

      setSchemaEndpoints(endpoints);
    } catch {
      setSchemaEndpoints([]);
    }
  }, []);

  useEffect(() => {
    if (!schemaId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [schemaRes, proxyConfigRes, profilesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/schemas/${schemaId}`),
          fetch(`${API_BASE_URL}/api/schemas/${schemaId}/proxy-config`),
          fetch(`${API_BASE_URL}/api/proxy-profiles`),
        ]);

        if (schemaRes.ok) {
          const data = await schemaRes.json();
          if (data.success && data.schema) {
            setSchema(data.schema);
            setTargetUrl(data.schema.testUrl || '');
            extractEndpoints(data.schema);

            if (data.schema.specCategory === 'postman') {
              try {
                const envRes = await fetch(`${API_BASE_URL}/api/postman/check/${schemaId}`);
                if (envRes.ok) {
                  const envData = await envRes.json();
                  setPostmanEnv(envData.environment || null);
                }
              } catch {
                /* ignore */
              }
            }
            restoreStatus();
          }
        }

        if (proxyConfigRes.ok) {
          const data = await proxyConfigRes.json();
          if (data.config) {
            setSchemaProxyConfig(data.config);
          }
        }

        if (profilesRes.ok) {
          const data = await profilesRes.json();
          setProxyProfiles(data.profiles || []);
        }
      } catch (error) {
        console.error('Failed to load specification details:', error);
        toast.error('Failed to load specification details');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [schemaId, extractEndpoints, restoreStatus]);

  // Auto-enable MLE params
  useEffect(() => {
    if (schemaEndpoints.length === 0) return;
    setSchemaProxyConfig((prev) => {
      const updated = { ...prev };
      let changed = false;
      schemaEndpoints.forEach((ep) => {
        const key = `${ep.method} ${ep.path}`;
        const epConfig = updated.endpoint_configs[key] || {};
        const currentMle = epConfig.mle_params || [];
        const encrypted = ep.parameters.filter((p: any) => p.mleEncrypted).map((p: any) => p.name);
        if (encrypted.length > 0 && currentMle.length === 0) {
          updated.endpoint_configs = { ...updated.endpoint_configs, [key]: { ...epConfig, mle_params: encrypted } };
          changed = true;
        }
      });
      return changed ? updated : prev;
    });
  }, [schemaEndpoints]);

  const handleSave = async () => {
    if (!schemaId) return;
    setSaving(true);
    try {
      const [proxyRes, schemaRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/schemas/${schemaId}/proxy-config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config: schemaProxyConfig }),
        }),
        fetch(`${API_BASE_URL}/api/schemas/${schemaId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ testUrl: targetUrl || null }),
        }),
      ]);
      if (proxyRes.ok && schemaRes.ok) {
        toast.success('Configuration saved successfully');
      } else {
        toast.error('Failed to save configuration');
      }
    } catch {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadPostmanEnv = async (parsed: any) => {
    if (!schemaId) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/postman/upload/${schemaId}/environment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      if (res.ok) {
        toast.success('Postman environment uploaded successfully');
        const checkRes = await fetch(`${API_BASE_URL}/api/postman/check/${schemaId}`);
        if (checkRes.ok) {
          const data = await checkRes.json();
          setPostmanEnv(data.environment || null);
        }
        triggerCheck();
      } else {
        toast.error('Failed to upload Postman environment');
      }
    } catch {
      toast.error('Failed to upload Postman environment');
    }
  };

  const updateEndpointProfile = (path: string, method: string, profileId: string) => {
    setSchemaProxyConfig((prev) => ({
      ...prev,
      endpoint_configs: {
        ...prev.endpoint_configs,
        [`${method} ${path}`]: { ...prev.endpoint_configs[`${method} ${path}`], proxy_profile_id: profileId },
      },
    }));
  };

  const toggleMleParameter = (path: string, method: string, paramName: string, enabled: boolean) => {
    setSchemaProxyConfig((prev) => {
      const key = `${method} ${path}`;
      const config = prev.endpoint_configs[key] || {};
      const current = config.mle_params || [];
      const next = enabled ? [...current, paramName] : current.filter((p: string) => p !== paramName);
      return { ...prev, endpoint_configs: { ...prev.endpoint_configs, [key]: { ...config, mle_params: next } } };
    });
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainSection}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={styles.container}>
        <div className={styles.mainSection}>
          <p>Specification not found.</p>
          <button className={styles.button} onClick={() => navigate('/specifications')}>
            {'\u2190'} Back to Specifications
          </button>
        </div>
      </div>
    );
  }

  const isPostman = schema.specCategory === 'postman';

  const getSpecServers = (): Array<{ url: string; description?: string }> => {
    try {
      const content = typeof schema.content === 'string' ? JSON.parse(schema.content) : schema.content;
      return content?.servers || [];
    } catch {
      return [];
    }
  };

  const servers = getSpecServers();

  return (
    <div className={styles.container}>
      <div className={styles.mainSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.titleGroup}>
            <h2 className={styles.title}>{schema.name}</h2>
            <p className={styles.pageDescription}>
              {isPostman ? 'Postman' : 'OpenAPI'} v{schema.version}
            </p>
          </div>
        </div>

        <div className={styles.proxyConfigSection}>
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}
          >
            <button className={styles.button} onClick={() => navigate('/specifications')}>
              {'\u2190'} Back to Specifications
            </button>
            <button className={styles.button} data-variant="success" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>

          {!isPostman && (
            <div className={styles.configCard}>
              <div className={styles.configHeader}>
                <h3>Target URL</h3>
                <p>Override the target server URL for all operations</p>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Target URL</label>
                <input
                  className={styles.input}
                  type="url"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder={servers[0]?.url || 'https://api.example.com'}
                />
                <p className={styles.helperText}>Leave empty to use the first server URL from the specification</p>
              </div>
              {servers.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <label className={styles.label} style={{ marginBottom: '0.5rem', display: 'block' }}>
                    Servers from specification
                  </label>
                  {servers.map((server, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '0.75rem',
                        background: '#f5f5f5',
                        marginBottom: '0.5rem',
                        borderLeft: '3px solid #3b82f6',
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{server.url}</div>
                      {server.description && (
                        <div style={{ fontSize: '0.875rem', color: '#666' }}>{server.description}</div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <UrlAvailabilityStatus
                checking={checking}
                checkProgress={checkProgress}
                checkResult={checkResult}
                onCheckAvailability={triggerCheck}
              />
            </div>
          )}

          {!isPostman && (
            <div className={styles.configCard}>
              <div className={styles.configHeader}>
                <h3>Default Proxy Profile</h3>
                <p>Select the default proxy profile for all endpoints in this schema</p>
              </div>
              <div className={styles.inputGroup}>
                <label className={styles.label}>Default Profile</label>
                <select
                  className={styles.select}
                  value={schemaProxyConfig.default_proxy_profile_id || ''}
                  onChange={(e) =>
                    setSchemaProxyConfig((prev) => ({ ...prev, default_proxy_profile_id: e.target.value || null }))
                  }
                >
                  <option value="">Direct (No Profile)</option>
                  {proxyProfiles.map((profile) => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {isPostman && (
            <PostmanEnvConfig
              postmanEnv={postmanEnv}
              loading={saving}
              onUploadEnvironment={handleUploadPostmanEnv}
              checking={checking}
              checkProgress={checkProgress}
              checkResult={checkResult}
              onCheckAvailability={triggerCheck}
            />
          )}

          <div className={styles.configCard}>
            <div className={styles.configHeader}>
              <h3>Context</h3>
              <p>Markdown context for this schema (for future use)</p>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.label}>Context (Markdown)</label>
              <textarea
                className={styles.textArea}
                value={schemaProxyConfig.context || ''}
                onChange={(e) => setSchemaProxyConfig((prev) => ({ ...prev, context: e.target.value }))}
                placeholder="Enter markdown context here..."
              />
            </div>
          </div>

          {!isPostman && (
            <div className={styles.configCard}>
              <div className={styles.configHeader}>
                <h3>Endpoint Configuration</h3>
                <p>Configure proxy profile and MLE parameters for each endpoint</p>
              </div>
              <EndpointConfigList
                schemaEndpoints={schemaEndpoints}
                schemaProxyConfig={schemaProxyConfig}
                proxyProfiles={proxyProfiles}
                onUpdateEndpointProfile={updateEndpointProfile}
                onToggleMleParameter={toggleMleParameter}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
